export type IssuePriority = "urgent" | "high" | "medium" | "low" | "none";

export type IssueType = "task" | "bug" | "feature" | "chore" | "epic";

export type StatusCategory = "todo" | "in_progress" | "done";

export type BoardKind = "project" | "multi_project";

export interface BoardStatus {
  id: string;
  // The project this status belongs to. A board sees the union of statuses across its projects.
  projectKey: string;
  name: string;
  category: StatusCategory;
}

// A board column: a named lane that maps to a SET of the board's available statuses. Columns are
// per-board, so the same project statuses can be grouped coarsely on one board and finely on another.
export interface BoardColumn {
  id: string;
  name: string;
  statusIds: string[];
  orderIndex: number;
}

export interface Project {
  key: string;
  name: string;
  color: string;
  nextIssueNumber: number;
  // The platform (designer) project this board project is tied to; drives artifact
  // autocomplete + screen navigation. Undefined until the board is initialized.
  platformProjectId?: string;
  // The project's workflow transition graph. Empty/absent = unrestricted (any move allowed).
  transitions?: BoardTransition[];
}

// A workflow transition rule. For a condition, `kind` is a performer (assignee | reporter); for a
// validator, a gate (assignee_required | estimate_required | subtasks_done | not_blocked).
export interface TransitionRule {
  kind: string;
  value?: string;
}

// An allowed move in a project's workflow: from one status to another (fromStatusId null = from any
// status), gated by conditions (who may, OR) and validators (preconditions, AND).
export interface BoardTransition {
  id: string;
  fromStatusId: string | null;
  toStatusId: string;
  name?: string;
  conditions: TransitionRule[];
  validators: TransitionRule[];
}

export interface LabelDef {
  name: string;
  color: string;
}

export interface Version {
  id: string;
  projectKey: string;
  name: string;
  releaseDate: string | null;
  released: boolean;
  // Optional plain-text release notes. Null/empty when unset.
  description: string | null;
}

// Per-release issue progress (GET /board/projects/{key}/version-progress). Only releases with at
// least one pinned issue get a row; absent versionId means zero pinned issues.
export interface VersionProgress {
  versionId: string;
  total: number;
  done: number;
}

// "user" stores a workspace member's id (resolved to name/avatar from the people directory).
export type CustomFieldType = "text" | "number" | "date" | "select" | "checkbox" | "user";

export interface CustomFieldDef {
  id: string;
  projectKey: string; // the project this field belongs to
  name: string;
  type: CustomFieldType;
  options: string[]; // for select
  // The issue types this field applies to. Empty = every type in the project (the default).
  issueTypes: IssueType[];
}

export type AutomationTrigger =
  | { kind: "status_changed_to"; statusId: string }
  | { kind: "assignee_empty"; statusId: string }
  | { kind: "issue_created"; statusId?: string }
  | { kind: "priority_changed_to"; priority: IssuePriority };

export type AutomationAction =
  | { kind: "assign_to_me" }
  | { kind: "add_label"; label: string }
  | { kind: "add_comment"; body: string }
  | { kind: "set_priority"; priority: IssuePriority }
  | { kind: "move_to_status"; statusId: string };

export interface AutomationRule {
  id: string;
  projectKey: string;
  name: string;
  enabled: boolean;
  // Whether a run notifies stakeholders (in-app + email). Off = silent (history still recorded).
  notifyOnRun: boolean;
  trigger: AutomationTrigger;
  action: AutomationAction;
  runCount?: number;
  lastRunAt?: string;
}

export const LABEL_COLOR_PALETTE: string[] = [
  "var(--blue-5)",
  "var(--green-6)",
  "var(--orange-5)",
  "var(--purple-5)",
  "var(--pink-5)",
  "var(--red-5)",
  "var(--yellow-6)",
  "var(--dark-7)"
];

export interface ProjectBoardScope {
  kind: "project";
  projectKey: string;
}

export interface MultiProjectBoardScope {
  kind: "multi_project";
  projectKeys: string[];
}

export type BoardScope = ProjectBoardScope | MultiProjectBoardScope;

// Kanban = continuous flow, no sprints. Scrum = sprint planning enabled.
// Absent mode is treated as "scrum" for backward compatibility.
export type BoardMode = "kanban" | "scrum";

export interface Board {
  id: string;
  // Boards have no code — they're addressed in the URL by a slug of their (unique) name; see
  // board-routing.ts. Projects own the code.
  name: string;
  scope: BoardScope;
  mode?: BoardMode;
  wipLimits?: Record<string, number | null>; // columnId -> max
  // Named lanes shown on this board, each mapping to a set of the board's available statuses.
  // Empty/absent = one column per available status (the default). Issues whose status is in no
  // column stay in the snapshot and are surfaced via a banner, never dropped.
  columns?: BoardColumn[];
}

// One emoji's reactions on a comment: the emoji + the user ids who reacted with it. The chip shows
// the count and highlights when the current user's id is in `userIds`; names/avatars resolve from
// the directory.
export interface CommentReaction {
  emoji: string;
  userIds: string[];
}

export interface IssueComment {
  id: string;
  issueId: string;
  authorUserId: string | null;
  bodyMarkdown: string;
  createdAt: string;
  editedAt: string | null;
  reactions: CommentReaction[];
}

// Server-unfurled metadata for a URL (og:* + <title>), shown as a discardable preview card under
// a comment. Fields are null when the page didn't provide them.
export interface LinkPreview {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  siteName: string | null;
}

// A single field's before→after change on an edit/status event, rendered as a mail-style diff.
export interface FieldChange {
  field: string;
  from: string;
  to: string;
}

export interface IssueActivity {
  id: string;
  issueId: string;
  actorUserId: string | null;
  text: string;
  at: string;
  // Present on field-edit/status events; null/absent for created/commented/attached/etc.
  changes?: FieldChange[];
}

export interface Attachment {
  id: string;
  issueId: string;
  fileName: string;
  mimeType: string;
  dataUrl: string;
  sizeBytes: number;
  createdAt: string;
}

// Server-computed analytics DTOs (Reports + Roadmap come from the backend).
export interface ReportBucket {
  week: string;
  count: number;
}

export interface ReportCumulativePoint {
  week: string;
  created: number;
  done: number;
}

export interface ReportPriorityCount {
  priority: IssuePriority;
  count: number;
}

export interface BoardReport {
  total: number;
  byCategory: Record<string, number>;
  throughput: ReportBucket[];
  cumulative: ReportCumulativePoint[];
  priorityBreakdown: ReportPriorityCount[];
}

// Sprint burndown: remaining work per day. `remaining` is null for days after today (the actual
// line stops at "now"); the ideal line spans the full window. Unit is points, or issue count when
// no issue in scope carries an estimate.
export interface SprintBurndown {
  sprintId: string;
  sprintName: string;
  startDate: string;
  endDate: string;
  unit: "points" | "issues";
  totalScope: number; // scope as of today
  committedScope: number; // scope at sprint start (the ideal baseline)
  remainingToday: number;
  points: BurndownPoint[];
}

export interface BurndownPoint {
  date: string;
  ideal: number;
  scope: number; // committed scope as of this day (rises when work is added mid-sprint)
  remaining: number | null;
  completed: number;
}

export interface RoadmapRow {
  epic: Issue;
  start: string;
  end: string;
  childrenCount: number;
  completedCount: number;
}

// A saved board view (filter/sort/group bookmark), scoped per user on the backend.
export interface BoardView {
  id: string;
  ownerUserId: string | null;
  name: string;
  href: string;
  createdAt: string;
}

// Backend-generated, per-recipient. The server decides who is notified (issue
// stakeholders minus the actor), so the frontend only ever receives its own.
export interface BoardNotification {
  id: string;
  recipientUserId: string | null;
  issueId: string;
  // The issue's human key, resolved server-side for the inbox link.
  issueKey: string;
  actorUserId: string | null;
  text: string;
  at: string;
  read: boolean;
}

export interface Issue {
  id: string;
  key: string;
  projectKey: string;
  type: IssueType;
  statusId: string;
  summary: string;
  descriptionMarkdown: string;
  priority: IssuePriority;
  assigneeUserId: string | null;
  reporterUserId: string | null;
  labels: string[];
  storyPoints: number | null;
  startDate: string | null;
  dueDate: string | null;
  // Sub-task parent (this issue is a sub-task OF it). Distinct from epicId.
  parentIssueId: string | null;
  // The epic this issue belongs to (epic link), or null. Separate from parentIssueId so the issue
  // stays on the board. Always null on epics themselves.
  epicId: string | null;
  sprintId: string | null;
  watcherUserIds: string[];
  archivedAt: string | null;
  originalEstimateMin: number | null;
  remainingEstimateMin: number | null;
  timeSpentMin: number | null;
  fixVersionId: string | null;
  customFields: Record<string, string>;
  // Epic-only metadata (null on non-epics): short name (falls back to summary) + swatch colour.
  epicName: string | null;
  epicColor: string | null;
  rank: string;
  createdAt: string;
  updatedAt: string;
}

export type SprintState = "future" | "active" | "closed";

export interface Sprint {
  id: string;
  projectKey: string;
  name: string;
  goal: string;
  state: SprintState;
  startDate: string | null;
  endDate: string | null;
  closedAt: string | null;
  createdAt: string;
}

export type ArtifactKind = "screen" | "component" | "datasource" | "action" | "workflow" | "report" | "api";

export interface ArtifactLink {
  id: string;
  issueId: string;
  kind: ArtifactKind;
  refId: string;
  displayName: string;
}

export const ARTIFACT_KIND_LABELS: Record<ArtifactKind, string> = {
  screen: "Screen",
  component: "Component",
  datasource: "Datasource",
  action: "Action",
  workflow: "Workflow",
  report: "Report",
  api: "API"
};

export const ARTIFACT_KIND_COLOR: Record<ArtifactKind, string> = {
  screen: "var(--blue-5)",
  component: "var(--purple-5)",
  datasource: "var(--green-6)",
  action: "var(--orange-5)",
  workflow: "var(--pink-5)",
  report: "var(--yellow-6)",
  api: "var(--dark-7)"
};

export type IssueLinkType = "blocks" | "blocked_by" | "relates_to" | "duplicates" | "duplicated_by";

export interface IssueLink {
  id: string;
  fromIssueId: string;
  toIssueId: string;
  type: IssueLinkType;
  createdAt: string;
}

export const LINK_TYPE_LABELS: Record<IssueLinkType, string> = {
  blocks: "Blocks",
  blocked_by: "Blocked by",
  relates_to: "Relates to",
  duplicates: "Duplicates",
  duplicated_by: "Duplicated by"
};

export const LINK_INVERSE: Record<IssueLinkType, IssueLinkType> = {
  blocks: "blocked_by",
  blocked_by: "blocks",
  relates_to: "relates_to",
  duplicates: "duplicated_by",
  duplicated_by: "duplicates"
};

// The monolithic BoardSnapshot was removed — the board never downloads all issues at once.
// Bounded lookups load via BoardConfig; issues/detail/search load on demand. See
// docs/plans/board-ondemand-loading.md.

// ── On-demand loading (replaces the monolithic snapshot) ──────────────────

// Bounded lookups for the board shell (GET /board/config). Same shape as the snapshot
// minus the per-issue data, which now loads on demand.
export interface BoardConfig {
  // Whether an owner has explicitly set up the board (drives the onboarding gate).
  initialized: boolean;
  projects: Project[];
  statuses: BoardStatus[];
  boards: Board[];
  labels: LabelDef[];
  sprints: Sprint[];
  versions: Version[];
  customFieldDefs: CustomFieldDef[];
  automations: AutomationRule[];
  // Whether the current user is a workspace owner and may change board configuration
  // (boards, columns, custom fields, automations). Backend enforces it regardless.
  canManage: boolean;
  // True when the workspace HAS board projects but the user can access none of them — so `projects`
  // is empty for an access reason, not because nothing is set up. Drives the "no access to any
  // project board" message instead of the onboarding / request-access notice.
  accessRestricted: boolean;
  // True when the current user has already requested board access in this workspace (persisted
  // server-side). Keeps the durable "access requested" state across refreshes instead of relying
  // on transient client state.
  accessRequested: boolean;
}

// Light issue projection for the grid/list (GET /board/{boardId}/issues). No description,
// custom fields, or per-issue children — just card fields + child counts.
export interface IssueCard {
  id: string;
  key: string;
  projectKey: string;
  type: IssueType;
  statusId: string;
  summary: string;
  priority: IssuePriority;
  assigneeUserId: string | null;
  reporterUserId: string | null;
  labels: string[];
  storyPoints: number | null;
  startDate: string | null;
  dueDate: string | null;
  parentIssueId: string | null;
  // The epic this card belongs to (epic link), or null. An epic's own card carries its name/colour;
  // child cards resolve those from the board's epic list (lanes, chips, the Epics panel).
  epicId: string | null;
  epicName: string | null;
  epicColor: string | null;
  sprintId: string | null;
  watcherUserIds: string[];
  fixVersionId: string | null;
  rank: string;
  createdAt: string;
  updatedAt: string;
  // Set only for archived issues (archived-issues modal); null on active cards.
  archivedAt: string | null;
  commentCount: number;
  attachmentCount: number;
  subtaskCount: number;
  // True when an unresolved blocking dependency exists — drives the card's blocked badge.
  isBlocked: boolean;
}

// One cursor-paginated page of a column. nextCursor is null when the column is exhausted.
export interface IssuePage {
  items: IssueCard[];
  nextCursor: string | null;
  total: number;
}

// The board's whole active working set for local-first sync (GET /{boardId}/sync). The client
// caches these cards and derives columns/filters/JQL/sort in memory. `truncated` is true when
// the working set exceeded the server cap — the client then falls back to per-column queries.
export interface BoardSyncResult {
  items: IssueCard[];
  total: number;
  truncated: boolean;
}

// Query params for a column page. statusId is the column; the rest mirror the board filters.
export interface IssueQueryParams {
  // A column's status set (Kanban column read). Falls back to the whole board when omitted.
  statusIds?: string[];
  q?: string;
  jql?: string;
  assignee?: string | null;
  priority?: IssuePriority | null;
  type?: IssueType | null;
  cursor?: string | null;
  limit?: number;
}

// Everything the issue detail view needs, fetched on open (GET /board/issues/{id}/detail).
// Attachments are metadata only; content loads via GET /board/attachments/{id}.
export interface IssueDetailBundle {
  issue: Issue;
  comments: IssueComment[];
  activity: IssueActivity[];
  attachments: Attachment[];
  links: IssueLink[];
  artifactLinks: ArtifactLink[];
  subtasks: Issue[];
  // Light cards for the issues referenced by `links`, for rendering the linked issue.
  linkedIssues: IssueCard[];
  // The sub-task parent as a light card (null unless this issue is itself a sub-task).
  parent: IssueCard | null;
  // The epic this issue belongs to (null when none) — drives the rail's Epic selection.
  epic: IssueCard | null;
  // When this issue IS an epic: its child issues (the "Child issues" section). Empty otherwise.
  epicChildren: IssueCard[];
}

// An epic for the Epics panel / group-by-epic resolver / picker: the epic card + child progress.
export interface EpicCard {
  epic: IssueCard;
  childCount: number;
  doneCount: number;
}

export interface CreateIssueInput {
  projectKey: string;
  type: IssueType;
  summary: string;
  statusId: string;
  priority?: IssuePriority;
  assigneeUserId?: string | null;
  labels?: string[];
  parentIssueId?: string | null;
  epicId?: string | null;
}

export interface UpdateIssueInput {
  summary?: string;
  descriptionMarkdown?: string;
  type?: IssueType;
  priority?: IssuePriority;
  assigneeUserId?: string | null;
  labels?: string[];
  storyPoints?: number | null;
  startDate?: string | null;
  dueDate?: string | null;
  sprintId?: string | null;
  parentIssueId?: string | null;
  epicId?: string | null;
  epicName?: string | null;
  epicColor?: string | null;
  originalEstimateMin?: number | null;
  remainingEstimateMin?: number | null;
  timeSpentMin?: number | null;
  fixVersionId?: string | null;
  customFields?: Record<string, string>;
  statusId?: string;
}

export interface MoveIssueInput {
  issueId: string;
  destinationStatusId: string;
  destinationIndex: number;
  // The column the card is leaving. Client-only (the backend ignores it) — used to emit a
  // precise issue.moved event with fromStatusId so the source column reloads even when there
  // was no optimistic state to fall back on (e.g. list/full-page moves).
  sourceStatusId?: string;
}

// Payload fired after every board mutation. Subscribers react only to the kinds/ids that
// touch their own slice (see use-column-issues / issue-detail). `statusId` fields are present
// only when the mutating call knows them; a column with no status hint reloads conservatively.
type BoardChangeEventBody =
  | { kind: "issue.created"; issueId: string; statusId?: string }
  // `patch` carries the authoritative card-field changes from the mutation response so a column
  // holding the card can update it in place without a refetch (see columnShouldReload callers).
  // `optimistic` marks the patch emitted BEFORE the round-trip (built from the input, no
  // statusId): the card/store patch in place for an instant edit, while reload-based views
  // (detail bundle, full-page header, roadmap, reports) ignore it and reconcile on the
  // authoritative event that follows. A non-optimistic, patch-less issue.updated is the
  // rollback signal — it reloads the affected views back to server truth.
  | { kind: "issue.updated"; issueId: string; statusId?: string; patch?: Partial<IssueCard>; optimistic?: boolean }
  | { kind: "issue.moved"; issueId: string; toStatusId?: string; fromStatusId?: string }
  | { kind: "issue.deleted"; issueId: string }
  | { kind: "issue.archived"; issueId: string }
  | { kind: "issue.restored"; issueId: string }
  // Subtask add/toggle/promote — affects the parent's detail. `issueId` is the parent when the
  // mutation knows it (some operations only have the subtask id; the open detail handles those).
  | { kind: "issue.subtaskChanged"; issueId?: string }
  // edit/delete only carry a comment/link/attachment id, not the issue id — so `issueId` is
  // optional and the open detail (the only consumer of these) reloads when it's absent or
  // matches its own issue. Columns ignore all three kinds.
  | { kind: "comment.changed"; issueId?: string }
  | { kind: "links.changed"; issueId?: string }
  | { kind: "attachments.changed"; issueId?: string }
  | { kind: "config.changed" };

// A board change event. `originClientId` is the tab that produced it: set when relayed to other
// tabs for multi-user live sync, so the originating tab can ignore the echo of its own change
// (it already applied it locally). The intersection layers it onto every discriminated variant.
export type BoardChangeEvent = BoardChangeEventBody & { originClientId?: string };

export const PRIORITY_ORDER: IssuePriority[] = ["urgent", "high", "medium", "low", "none"];

export const ISSUE_TYPE_LABELS: Record<IssueType, string> = {
  task: "Task",
  bug: "Bug",
  feature: "Story",
  chore: "Chore",
  epic: "Epic"
};

// Issue types in display order — for pickers that let a custom field choose which types it applies to.
export const ISSUE_TYPES: IssueType[] = ["task", "feature", "bug", "chore", "epic"];

export const PRIORITY_LABELS: Record<IssuePriority, string> = {
  urgent: "Urgent",
  high: "High",
  medium: "Medium",
  low: "Low",
  none: "No priority"
};

// Single source of truth for the priority dot/bar color. These were copy-pasted in four files and
// had drifted — `medium` was grey in most places but blue in the drawer, and `none` was --dark-3 in
// some and --dark-4 in others. Canonical: medium stays grey (blue is the in-progress status color),
// none is the faint --dark-4 (matching the "todo" status dot).
export const PRIORITY_COLOR: Record<IssuePriority, string> = {
  urgent: "var(--red-5)",
  high: "var(--orange-5)",
  medium: "var(--dark-7)",
  low: "var(--dark-5)",
  none: "var(--dark-4)"
};

// Priority sort weight (urgent first). Was redefined as a local PRIORITY_RANK in several files.
export const PRIORITY_RANK: Record<IssuePriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4
};

// Status-category dot color — the kanban column-header dot and the list/backlog status dots. Was an
// identical map copy-pasted in three files.
export const STATUS_DOT_COLOR: Record<StatusCategory, string> = {
  todo: "var(--dark-4)",
  in_progress: "var(--blue-5)",
  done: "var(--green-6)"
};

export function isMultiProjectBoard(board: Board): board is Board & { scope: MultiProjectBoardScope } {
  return board.scope.kind === "multi_project";
}

export function boardProjectKeys(board: Board): string[] {
  return board.scope.kind === "project" ? [board.scope.projectKey] : board.scope.projectKeys;
}
